<script setup lang="ts">
import type { PropType } from "vue";
import type { UIColor } from "@align/ui";

const t = useTranslator();
const filter = useFilter();

const props = defineProps({
  type: {
    type: String as PropType<"ASSET_MANAGEMENT_PORTFOLIO" | "ASSET_MANAGEMENT_PROPERTY" | "ASSET_MANAGEMENT_TENANCY">,
    required: true,
  },

  labelText: {
    type: String,
    required: true,
  },

  fields: {
    type: Object,
    default: null,
  },

  selected: {
    type: Boolean,
    default: false,
  },

  clearIconVisible: {
    type: Boolean,
    required: true,
  },

  highlightText: {
    type: String,
    default: "",
  },
});

const content = ref<HTMLElement>();

const scrollIntoView = () => {
  const parent = content.value?.parentNode as HTMLElement | undefined;

  parent?.scrollIntoView();
};

const theme = {
  color: "--color-primary" as UIColor,
  colorHoverEffectBackground: "--color-accent-100" as UIColor,
  colorHoverEffectBorder: "--color-accent-300" as UIColor,
  colorHighlight: "--color-accent-200" as UIColor,
};

const iconId = computed(() => {
  switch (props.type) {
    case "ASSET_MANAGEMENT_PORTFOLIO":
      return "work";
    case "ASSET_MANAGEMENT_PROPERTY":
      return "icon-property";
    case "ASSET_MANAGEMENT_TENANCY":
      return "icon-condo";
    default:
      return "portfolio";
  }
});

const labelHighlighted = (input: string | number) => {
  let s = input?.toString() || "";

  if (s.length > 0) {
    const r = new RegExp(props.highlightText, "i");

    const m = s.match(r);

    if (m && m[0]) {
      const colorHighlight = theme.colorHighlight;
      const colorHoverEffectBorder = theme.colorHoverEffectBorder;

      const h = `<span
              style="
                margin-right: 0px;
                text-decoration-style: wavy;
                background-color: var(${colorHighlight});
                outline: dotted 1px var(${colorHoverEffectBorder});
              ">${m[0]}</span>`;

      return s.replace(r, h).trim();
    }

    return s.trim();
  }

  return s.trim();
};

const selected = computed(() => props.selected);

watch(selected, (val) => val && scrollIntoView());
</script>

<template>
  <SimpleTableRow
    :hoverEffectColorBackground="theme.colorHoverEffectBackground"
    :hoverEffectColorBorder="theme.colorHoverEffectBorder"
    :hoverEffectStateActive="selected"
    :class="$style.simpleTableRow"
    @click="$emit('click', $event)"
  >
    <SimpleTableCell>
      <div ref="content" :class="$style.content">
        <KeyValuePair direction="horizontal">
          <template #containerStart>
            <Icon :id="iconId" :color="theme.color" size="medium" />
          </template>

          <template #containerEnd>
            <div style="display: flex; align-items: center">
              <Copy type="label" size="small" :color="theme.color">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="labelHighlighted(labelText)" />
              </Copy>

              <CompositionSpacer direction="horizontal" size="small" />

              <template v-if="fields">
                <KeyValuePairContainerRow omitPadding>
                  <KeyValuePair v-if="fields.updated" direction="horizontal">
                    <template #containerStart>
                      <Copy type="label" size="small" color="--color-accent-300">
                        {{ t("CHROME_SEARCH_ITEM_COMPANY") }}
                      </Copy>
                    </template>

                    <template #containerEnd>
                      <Copy type="bodyInLine" size="medium" color="--color-accent-300">
                        {{ filter.date.short(fields.updated) }}
                      </Copy>
                    </template>
                  </KeyValuePair>

                  <KeyValuePair v-if="fields.external_id" direction="horizontal" renderAsLabel labelColorNamespace="accent">
                    <template #containerStart>
                      <Copy type="label" size="small" color="--color-accent-500"> ID </Copy>
                    </template>

                    <template #containerEnd>
                      <Copy type="bodyInLine" size="medium" color="--color-accent-500">
                        {{ fields.external_id }}
                      </Copy>
                    </template>
                  </KeyValuePair>

                  <KeyValuePair v-if="fields.portfolio" direction="horizontal" renderAsLabel labelColorNamespace="accent">
                    <template #containerStart>
                      <Copy type="label" size="small" color="--color-accent-500"> {{ t("CHROME_SEARCH_ITEM_PORTFOLIO") }} </Copy>
                    </template>

                    <template #containerEnd>
                      <Copy type="bodyInLine" size="medium" color="--color-accent-500">
                        {{ fields.portfolio }}
                      </Copy>
                    </template>
                  </KeyValuePair>

                  <KeyValuePair v-if="fields.type" direction="horizontal" renderAsLabel labelColorNamespace="accent">
                    <template #containerStart>
                      <Copy type="label" size="small" color="--color-accent-500"> Type </Copy>
                    </template>

                    <template #containerEnd>
                      <Copy type="bodyInLine" size="medium" color="--color-accent-500">
                        {{ filter.capitalize(fields.type) }}
                      </Copy>
                    </template>
                  </KeyValuePair>
                </KeyValuePairContainerRow>
              </template>
            </div>
          </template>
        </KeyValuePair>

        <div v-if="clearIconVisible" :title="t('CHROME_HISTORY_TITLE_CLEAR_ITEM')" :class="$style.delete" @click.stop="$emit('click-clear', $event)">
          <Icon id="close" size="medium" :color="theme.color" />
        </div>
      </div>
    </SimpleTableCell>
  </SimpleTableRow>
</template>

<style module>
.content {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.simpleTableRow:hover {
  cursor: pointer;
}

.delete {
  display: inline-flex;
  transition-property: transform;
  transition-duration: var(--transition-duration-shortest);
  transition-timing-function: ease-in-out;
}

.delete:hover {
  transform: scale(1.2);
}
</style>
